import { Link } from 'react-router-dom';
import { message, Modal, Button, Image, Divider, Tooltip, Popconfirm, Tag } from 'antd';
import { config } from "util/config";
import { httpClient } from "util/Api";
import CustomBadge from 'components/CustomBadge';
import CustomImage from 'components/CustomImage';
import { operationalFunction } from 'components/UIBuilder/SectionFunction/Common';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import JSZip from 'jszip';

//设置token
export function setToken(token): void {
  localStorage.setItem('token', token);
}

//取得token
export function getToken() {
  return localStorage.getItem('token');
}

//原字符串, 保留长度, 超出时的省略字符
export function truncateString(str, length, ellipsis) {
  ellipsis = ellipsis || '';
  return length !== undefined && str !== undefined && str !== null ? str.substring(0, length) + (length < str.length ? ellipsis : '') : str;
}

export function roundToDecimalPlaces(number, places) {
  // 确保 places 是一个整数
  places = Math.trunc(places);

  // 处理负数和无限值
  if (isNaN(number) || !isFinite(number)) {
    return NaN;
  }

  // 处理四舍五入的情况
  const factor = Math.pow(10, places);
  const roundedNumber = Math.round((number + Number.EPSILON) * factor) / factor;

  // 处理小数位数不足的情况
  return Number(roundedNumber.toFixed(places));
}

//原数值, 保留几位小数, 货币符号, 千位分隔符, 小数分隔符
export function formatMoney(number, places, symbol, thousand, decimal) {
  // 确定是否是负数，并在处理过程中使用绝对值
  let isNegative = number < 0;
  if (number < 0 && number > -0.0001) {
    number = 0;
  }
  places = !isNaN(places = Math.abs(places)) ? places : 2;
  symbol = symbol || '$';
  thousand = thousand || ',';
  decimal = decimal || '.';

  // 对数字进行四舍五入，并转换为整数部分的字符串形式
  let roundedNumber = roundToDecimalPlaces(number, places);
  let i = parseInt(Math.abs(roundedNumber), 10) + '';

  // 处理千分位分隔符
  let j = i.length > 3 ? i.length % 3 : 0;
  let formattedNumber = (j ? i.substr(0, j) + thousand : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousand);

  // 添加小数点及小数部分
  formattedNumber += places ? decimal + Math.abs(roundedNumber - i).toFixed(places).slice(2) : '';

  // 添加货币符号和负号
  return symbol + (isNegative ? '-' : '') + formattedNumber;
}

//为货币控件格式化显示
export function formatMoneyForControl(value, info, places, allowNegative) {
  allowNegative = allowNegative || false;
  if (isNullOrEmpty(value)) {
    return '';
  }

  // 删除非数字字符
  if (allowNegative) {
    value = value.replace(/[^\d-.]/g, '');
  } else {
    value = value.replace(/[^\d.]/g, '');
  }

  if (info.userTyping) {
    // 查找字符串中的第一个小数点的位置
    const dotIndex = value.indexOf('.');
    if (dotIndex >= 0) {
      // 如果存在小数点，只保留小数点后两位，并删除其他的小数点
      value = value.substr(0, dotIndex + (places + 1)).replace(/\.(?=.*\.)/g, '');
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  } else {
    return `${value !== undefined ? parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places }) : ''}`;
  }
  return parseFloat(value);
}

//为货币控件格式化数值
export function parserMoneyForControl(value, allowNegative) {
  allowNegative = allowNegative || false;
  if (!allowNegative) {
    return parseFloat(value.replace(/[-,]/g, ''));
  } else {
    return parseFloat(value.replace(/[,]/g, ''));
  }
}

//数值格式化成文件大小
export function formatFileSize(fileSize, places) {
  places = places || 1;
  if (fileSize === 0) return '0 Bytes';
  const k = 1024;
  const dm = places < 0 ? 0 : places;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(fileSize) / Math.log(k));
  return parseFloat((fileSize / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

//字符串转DateTime, 当前为澳洲日期格式, 根据项目改写此方法
export function stringToDateTime(str) {
  try {
    var type = str.split(' ');
    var d = type[0].split('/');
    var t = type[1].split(':');
    return new Date(d[2], d[1] - 1, d[0], t[0] !== undefined ? t[0] : 0, t[1] !== undefined ? t[1] : 0, t[2] !== undefined ? t[2] : 0);
  } catch {
    return new Date(1970, 0, 1);
  }
}

//获取年月日的时间戳
export function formatDateToVersion() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 因为月份是从0开始的，所以需要+1
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

//根据每个单字段的类型, 对生成单元格的数据进行格式化
export function formatUieData({ uie, data, record, companyId, sectionComponent, pageComponent, key }) {
  if (key === undefined || key === null) {
    key = record.key;
  }
  if (uie.key !== null) {
    key = record[uie.key];
  }
  switch (uie.type) {
    case 'date':
      return <>{truncateString(data, 10)}</>
    case 'bool':
      return data ? 'Yes' : 'No'
    case 'money':
      return <>{formatMoney(data)}</>
    case 'fileSize':
      return <>{formatFileSize(data)}</>
    case 'pageLink':
      if (record[uie.color] !== null && record[uie.color] !== undefined) {
        return <Link target={uie.target} to={{ pathname: uie.href + '/' + key, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}><CustomBadge text={data} backgroundColor={record[uie.color]} /></Link>
      } else {
        return <Link target={uie.target} to={{ pathname: uie.href + '/' + key, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
      }
    case 'clientPageLink':
      return <Link target="_blank" to={{ pathname: config.clientUrl + uie.href + '/' + key, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
    case 'clientTrackingPageLink':
      return <>
        <Link title='Click to Open' target="_blank" to={{ pathname: config.clientUrl + '/job/public/' + record.AccessCode, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
        <i className="fal fa-copy ml-2"
          style={{ cursor: 'pointer' }}
          onClick={async (e: any) => {
            let text = config.clientUrl + '/job/public/' + record.AccessCode;
            message.info('Copied to clipboard!');
            await navigator.clipboard.writeText(text);
          }}
          title="Copy link"></i>
      </>
    case 'clientJobDetailLink':
      let href = '/job/detail';
      if (record.WorkflowStatus.InitStatus !== null) {
        href = '/job/new';
      }
      if (record[uie.color] !== null && record[uie.color] !== undefined) {
        return <Link target={uie.target} to={{ pathname: href + '/' + key, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}><CustomBadge text={data} backgroundColor={record[uie.color]} /></Link>
      } else {
        return <Link target={uie.target} to={{ pathname: href + '/' + key, companyId: companyId, areaName: uie.areaName, pageName: uie.pageName, key: key }}>{data}</Link>
      }
    case 'document':
      return record.Stamped === true ? <span style={{ cursor: 'pointer' }} onClick={() => { download({ url: '/Business/Document/Download/' + key, data: null, filename: data }) }}><CustomBadge text={data} backgroundColor={pageComponent.state.stampedDocColor} /></span> : <span className='link' onClick={() => { download({ url: '/Business/Document/Download/' + key, data: null, filename: data }) }}>{data}</span>
    case 'clientDocument':
      return record.Stamped === true ? <span style={{ cursor: 'pointer' }} onClick={() => { download({ url: '/Business/Document/Download/' + record.DocumentId, data: null, filename: data }) }}><CustomBadge text={data} backgroundColor={pageComponent.state.stampedDocColor} /></span> : <span className='link' onClick={() => { download({ url: '/Business/Document/Download/' + record.DocumentId, data: null, filename: data }) }}>{data}</span>
    case 'publicDocument':
      return record.Stamped === true ? <span style={{ cursor: 'pointer' }} onClick={() => { download({ url: '/Business/Document/PublicDownload/' + key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: data }) }}><CustomBadge text={data} backgroundColor={pageComponent.state.stampedDocColor} /></span> : <span className='link' onClick={() => { download({ url: '/Business/Document/PublicDownload/' + key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: data }) }}>{data}</span>
    case 'documentShare':
      let html = '';
      if (record.ShareClientPortal) {
        html += '<span class="circular" style="background: #00A143; margin-right: .25rem;" title="Client">C</span>';
      }
      if (record.ShareTrackingPage) {
        html += '<span class="circular" style="background: #E91E63; margin-right: .25rem;" title="Tracking Page">T</span>';
      }
      if (record.ShareOnsiteJob) {
        html += '<span class="circular" style="background: #2196F3; margin-right: .25rem;" title="Onsite Job">O</span>';
      }
      return <span dangerouslySetInnerHTML={{ __html: html }} />
    case 'badge':
      switch (uie.component) {
        case 'Button':
          return <><Button
            icon={<CustomBadge text={data} backgroundColor={record[uie.color]} />}
            type="link"
            style={{ width: 'auto' }}
            onClick={(e: any) => { operationalFunction({ e: e, functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
            onContextMenu={(e: any) => { operationalFunction({ e: e, functionName: uie.onContextMenu, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
          /> {record.Loading === true ? <img className="loading-icon-inline" src="/assets/images/icon/loading.svg" /> : <></>}</>
        default:
          switch (uie.source) {
            case 'WorkflowStatusName':
              return isNullOrEmpty(record.WorkflowStatusDescription) ? <CustomBadge text={data} backgroundColor={record[uie.color]} /> : <Tooltip mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title={<span dangerouslySetInnerHTML={{ __html: record.WorkflowStatusDescription?.replace(/\n/g, '<br />') }}></span>}>
                <span><CustomBadge text={data} backgroundColor={record[uie.color]} /></span>
              </Tooltip >
            case 'AccountingApp':
              if (record.ShowPayButton) {
                return data !== null && data !== undefined ? <><CustomBadge text={data} backgroundColor={record[uie.color]} /> <CustomBadge text={<Link style={{ color: '#ffffff' }} target="_blank" to={{ pathname: config.staffUrl + '/pay/mpgs/invoice/' + record.EncryptedInvoiceId }}>Pay Online</Link>} /></> : <></>
              } else {
                return data !== null && data !== undefined ? <CustomBadge text={data} backgroundColor={record[uie.color]} /> : <></>
              }
            default:
              return data !== null && data !== undefined ? <CustomBadge text={data} backgroundColor={record[uie.color]} /> : <></>
          }
      }
    case 'html':
      return record.edit === true ?
        <span className='full-in-parent'>
          <CKEditor
            data={data}
            editor={DecoupledEditor}
            onReady={editor => {
              editor.ui.getEditableElement().parentElement.insertBefore(
                editor.ui.view.toolbar.element,
                editor.ui.getEditableElement(),
                editor.editing.view.focus()
              );
            }}
            onChange={(event, editor) => {
            }}
            onBlur={(event, editor) => {
              record[uie.source] = editor.getData();
              operationalFunction({ functionName: uie.onChange, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent })
            }}
            onFocus={(event, editor) => {
            }}
            config={config.component.CKEditor.config}
          />
        </span> :
        <span className='full-in-parent'
          onClick={() => { operationalFunction({ functionName: uie.onClick, sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
          dangerouslySetInnerHTML={{ __html: data }} />
    case 'JobChecklistComments':
      if (record.JobChecklistImageList !== null && record.JobChecklistImageList.length > 0) {
        return <>
          <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />
          <Image.PreviewGroup>
            {
              record.JobChecklistImageList.map((image, i) => {
                return <CustomImage key={i}
                  divClassName="job-checklist-image-container"
                  imageClassName="job-checklist-image"
                  src={getByteImageSrc(image)}
                />
              })
            }
          </Image.PreviewGroup>
        </>
      } else {
        return <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />;
      }
    case 'JobChecklistClientComment':
      return <>
        <>{data}</>
        {
          record.JobChecklistClientDocumentList !== null && record.JobChecklistClientDocumentList.length > 0 ?
            <>
              <Divider />
              {
                record.JobChecklistClientDocumentList.map((document, i) =>
                  <div key={i}>
                    <span
                      className='report-span'
                      style={{
                        color: '#007bff',
                        border: '1px solid #007bff',
                        fontSize: '0.8rem',
                        padding: '0.25em 0.4em',
                        borderRadius: '0.25rem',
                        display: 'inline-flex',
                        verticalAlign: 'baseline',
                        lineHeight: '18px',
                        margin: '0 6px 6px 0',
                        cursor: 'pointer'
                      }}>
                      <span onClick={() => { download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name }) }}>{document.Name}</span>
                      <span style={{ margin: '0 4px 0 4px' }} >|</span>
                      <Popconfirm
                        placement="bottom"
                        title={config.component.defaultDeleteConfirmText}
                        onConfirm={() => { operationalFunction({ functionName: 'deleteJobChecklistClientDocument', sectionName: 'JobChecklist', parameter: document, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                      >
                        <DeleteOutlined style={{ fontSize: '18px' }} />
                      </Popconfirm>
                    </span>
                  </div>
                )
              }
            </> : <></>
        }
        <div style={{ display: 'flex', marginTop: '10px' }}>
          <Button
            type="primary"
            onClick={() => { operationalFunction({ functionName: 'editBySection', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
          >Edit Your Comment</Button>
          <Button
            type="primary"
            style={{ marginLeft: '20px' }}
            onClick={() => { operationalFunction({ functionName: 'uploadDocument', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
          >Upload Document</Button>
        </div>
      </>
    case 'PublicJobChecklistClientComment':
      if (record.JobChecklistClientDocumentList !== null && record.JobChecklistClientDocumentList.length > 0) {
        return <>
          <>{data}</>
          <Divider />
          {
            record.JobChecklistClientDocumentList.map((document, i) =>
              <div key={i}>
                <span
                  className='report-span'
                  style={{
                    color: '#007bff',
                    border: '1px solid #007bff',
                    fontSize: '0.8rem',
                    padding: '0.25em 0.4em',
                    borderRadius: '0.25rem',
                    display: 'inline-flex',
                    verticalAlign: 'baseline',
                    lineHeight: '18px',
                    margin: '0 6px 6px 0',
                    cursor: 'pointer'
                  }}>
                  <span onClick={() => { download({ url: '/Business/Document/PublicDownload/' + document.key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: document.Name }) }}>{document.Name}</span>
                  {/* <span style={{ margin: '0 4px 0 4px' }} >|</span>
                  <Popconfirm
                    placement="bottom"
                    title={config.component.defaultDeleteConfirmText}
                    onConfirm={() => { operationalFunction({ functionName: 'deletePublicJobChecklistClientDocument', sectionName: 'JobChecklist', parameter: document, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                  >
                    <DeleteOutlined style={{ fontSize: '18px' }} />
                  </Popconfirm> */}
                </span>
              </div>
            )
          }
        </>
      } else {
        return <>{data}</>;
      }
    case 'PublicJobChecklistClientCommentOnly':
      // if (record.JobChecklistClientDocumentList !== null && record.JobChecklistClientDocumentList.length > 0) {
      return <>
        {data}
        {/* <Divider /> */}
        <Button
          type="primary"
          style={{ display: 'block' }}
          onClick={() => { operationalFunction({ functionName: 'publicEditBySection', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
        >Edit Your Comment</Button>
      </>
    // } else {
    //   return <>{data}</>;
    // }
    case 'PublicJobChecklistClientDocument':
      // if (record.JobChecklistClientDocumentList !== null && record.JobChecklistClientDocumentList.length > 0) {
      return <>
        {
          record.JobChecklistClientDocumentList.map((document, i) =>
            <div key={i}>
              <span
                className='report-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span onClick={() => { download({ url: '/Business/Document/PublicDownload/' + document.key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: document.Name }) }}>{document.Name}</span>
                {/* <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deletePublicJobChecklistClientDocument', sectionName: 'JobChecklist', parameter: document, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm> */}
              </span>
            </div>
          )
        }
        {/* <Divider /> */}
        <Button
          type="primary"
          style={{ display: 'block' }}
          onClick={() => { operationalFunction({ functionName: 'publicUploadDocument', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
        >Upload Document</Button>
      </>
    // } else {
    //   return <>{data}</>;
    // }
    case 'JobRequiredCertificateClientDocument':
      return <>
        {
          record.JobRequiredCertificateClientDocumentList.map((document, i) =>
            <div key={i}>
              <span
                className='report-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span onClick={() => { download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name }) }}>{document.Name}</span>
                <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deleteJobRequiredCertificateClientDocument', sectionName: 'JobRequiredCertificate', parameter: document, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm>
              </span>
            </div>
          )
        }
        <Button
          type="primary"
          style={{ display: 'block' }}
          onClick={() => { operationalFunction({ functionName: 'publicUploadDocument', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }} >Upload Document</Button >
      </>

    case 'PublicJobRequiredCertificateClientDocument':
      // if (record.JobRequiredCertificateClientDocumentList !== null && record.JobRequiredCertificateClientDocumentList.length > 0) {
      return <>
        {
          record.JobRequiredCertificateClientDocumentList.map((document, i) =>
            <div key={i}>
              <span
                className='report-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span onClick={() => { download({ url: '/Business/Document/PublicDownload/' + document.key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: document.Name }) }}>{document.Name}</span>
                {/* <span style={{ margin: '0 4px 0 4px' }} >|</span>
                  <Popconfirm
                    placement="bottom"
                    title={config.component.defaultDeleteConfirmText}
                    onConfirm={() => { operationalFunction({ functionName: 'deletePublicJobRequiredCertificateClientDocument', sectionName: 'JobRequiredCertificate', parameter: document, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                  >
                    <DeleteOutlined style={{ fontSize: '18px' }} />
                  </Popconfirm> */}
              </span>
            </div>
          )
        }
        {/* <Divider /> */}
        <Button
          type="primary"
          style={{ display: 'block' }}
          onClick={() => { operationalFunction({ functionName: 'publicUploadDocument', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
        >Upload Document</Button>
      </>
    // } else {
    //   return <>{data}</>;
    // }
    case 'SubJobChecklistComments':
      if (record.SubJobChecklistImageList !== null && record.SubJobChecklistImageList.length > 0) {
        return <>
          <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />
          <Image.PreviewGroup>
            {
              record.SubJobChecklistImageList.map((image, i) => {
                return <CustomImage key={i}
                  divClassName="subjob-checklist-image-container"
                  imageClassName="subjob-checklist-image"
                  src={getByteImageSrc(image)}
                  buttons={
                    <>
                      <Button
                        icon={<img className="svg-img-btn" src={image.ShowOnReport ? 'assets/images/icon/verified_green.svg' : 'assets/images/icon/verified.svg'} />}
                        type='link'
                        className='image-button-div-cion'
                        onClick={() => { operationalFunction({ functionName: 'changeSubJobChecklistImageShowOnReport', sectionName: sectionComponent.props.sectionName, parameter: image, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
                      />
                      <Popconfirm
                        placement="bottom"
                        title={config.component.defaultDeleteConfirmText}
                        onConfirm={() => { operationalFunction({ functionName: 'deleteSubJobChecklistImage', sectionName: sectionComponent.props.sectionName, parameter: image, sectionComponent: sectionComponent, pageComponent: pageComponent, uie: uie }) }}
                      >
                        <Button
                          icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} />}
                          type='link'
                          className='image-button-div-cion'
                        />
                      </Popconfirm>
                    </>}
                />
              })
            }
          </Image.PreviewGroup>
        </>
      } else {
        return <span className='full-in-parent' dangerouslySetInnerHTML={{ __html: data }} />;
      }
    case 'MailMergeTemplateName':
      return record.Docusign ? <>{data}<i className="fal fa-file-signature table-icon docusign-icon svg-img-btn"></i></> : <>{data}</>
    case 'ContactNumber':
      let contactNumber = [];
      if (!isNullOrEmpty(record.Phone)) contactNumber.push(record.Phone);
      if (!isNullOrEmpty(record.Phone) && !isNullOrEmpty(record.Mobile)) contactNumber.push(<Divider key={key + '_Divider'} type="vertical" />);
      if (!isNullOrEmpty(record.Mobile)) {
        let result = false;
        config.sms.mobileStartWith.map(item => record.Mobile.startsWith(item)).map(item => {
          if (item) {
            result = true;
          }
        })
        if (result) {
          contactNumber.push(
            <span key={key + '_span'} className='link'
              onClick={(e: any) => { operationalFunction({ e: e, functionName: 'sendSMS', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
            >{record.Mobile}</span>
          );
        } else {
          contactNumber.push(record.Mobile);
        }
      }
      return <>{contactNumber}</>
    case 'JobLabel':
      return <>{
        record[uie.source].map((item, i) => <span key={i} className="job-label"><CustomBadge text={item.Name} backgroundColor={item.BackgroundColor} verticalAlign="top" /></span>)
      }</>
    case 'JobNoteContent':
      if (record.ClientPortal) {
        return <><CustomBadge text={<span style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: truncateString(removeHtmlTags(data), 200, '...').replace(/\n/g, '<br />') }} />} display="inline-block" backgroundColor="#00A622" verticalAlign="top" /></>
      } else {
        return <span dangerouslySetInnerHTML={{ __html: truncateString(removeHtmlTags(data), 200, '...').replace(/\n/g, '<br />') }} />
      }
    case 'LandAttribute':
      return <>
        {record.PptTermite ? <img className="svg-img-btn mr-2" title="Termite" src={'assets/images/icon/termite.svg'} /> : <></>}
        {record.PptFloodProne ? <img className="svg-img-btn mr-2" title="Flood Prone" src={'assets/images/icon/flood.svg'} /> : <></>}
        {record.PptBushfireProne ? <img className="svg-img-btn mr-2" title="Bushfire Prone" src={'assets/images/icon/fire.svg'} /> : <></>}
        {record.PptAlpineArea ? <img className="svg-img-btn mr-2" title="Alpine Area" src={'assets/images/icon/snowflake.svg'} /> : <></>}
        {record.PptSolarHotWater ? <img className="svg-img-btn mr-2" title="Solar Hot Water" src={'assets/images/icon/solar-panel.svg'} /> : <></>}
        {record.PptRainwaterTank ? <img className="svg-img-btn mr-2" title="Rainwater Tank" src={'assets/images/icon/water-tank.svg'} /> : <></>}
      </>
    case 'HideOnMailMerge':
      return record.HideOnMailMerge === true ? <span style={{ color: '#a7a7a7' }}>{data}</span> : <>{data}</>
    case 'LgaInfo':
      return !isNullOrEmpty(data) ? <>
        <span className='link'
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'sendEmail', sectionName: sectionComponent.props.sectionName, parameter: record.Lga, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        >{data}</span>
        <i className="fal fa-info-circle link ml-2"
          onMouseEnter={(e) => {
            pageComponent.setState({ showLgaInfo: true, mouseEvent: e });
          }}
        />
      </> : <></>
    case 'BookingRequest':
      return <div>
        <i className="fal fa-calendar-alt BookingRequest mr-2"
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'editBookingRequest', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        />
        <span dangerouslySetInnerHTML={{ __html: data }} />
      </div>
    case 'PublicBookingRequest':
      return <div>
        <span dangerouslySetInnerHTML={{ __html: data }} />
        <Button
          type="primary"
          style={{ display: 'block' }}
          onClick={(e: any) => { operationalFunction({ e: e, functionName: 'editBookingRequest', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
        >Booking Request</Button>
      </div>
    case 'SubJobReport':
      return <>
        {
          record.SubJobReportList.map((document, i) =>
            <div key={i}>
              <span
                className='report-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span onClick={() => {
                  if (pageComponent.props.accessCode === undefined) {
                    download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name });
                  } else {
                    download({ url: '/Business/Document/PublicDownload/' + document.key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: document.Name });
                  }
                }}>{document.Name}</span>
                {/* <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deleteSubJobReport', sectionName: 'SubJob', parameter: { SubJobId: record.SubJobId, DocumentId: document.DocumentId }, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm> */}
              </span>
            </div>
          )
        }
      </>
    case 'PublicSubJobReport':
      return <>
        {
          record.SubJobReportList.map((document, i) =>
            <div key={i}>
              <span
                className='report-span'
                style={{
                  color: '#007bff',
                  border: '1px solid #007bff',
                  fontSize: '0.8rem',
                  padding: '0.25em 0.4em',
                  borderRadius: '0.25rem',
                  display: 'inline-flex',
                  verticalAlign: 'baseline',
                  lineHeight: '18px',
                  margin: '0 6px 6px 0',
                  cursor: 'pointer'
                }}>
                <span onClick={() => {
                  if (pageComponent.props.accessCode === undefined) {
                    download({ url: '/Business/Document/Download/' + document.key, data: null, filename: document.Name });
                  } else {
                    download({ url: '/Business/Document/PublicDownload/' + document.key + '?accessCode=' + pageComponent.props.accessCode, data: null, filename: document.Name });
                  }
                }}>{document.Name}</span>
                {/* <span style={{ margin: '0 4px 0 4px' }} >|</span>
                <Popconfirm
                  placement="bottom"
                  title={config.component.defaultDeleteConfirmText}
                  onConfirm={() => { operationalFunction({ functionName: 'deleteSubJobReport', sectionName: 'SubJob', parameter: { SubJobId: record.SubJobId, DocumentId: document.DocumentId }, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
                >
                  <DeleteOutlined style={{ fontSize: '18px' }} />
                </Popconfirm> */}
              </span>
            </div>
          )
        }
      </>
    case 'reportLink':
      return <span className='link'
        onClick={(e: any) => { operationalFunction({ e: e, functionName: 'showReportDiv', sectionName: sectionComponent.props.sectionName, parameter: record, sectionComponent: sectionComponent, pageComponent: pageComponent }) }}
      >{data}</span>;
    case 'ClientDueDate':
      return <><span> {record.ClientDueDate !== null ? record.ClientDueDate : ''} {record.ClientDueDateNote}</span></>;
    default:
      return <>{data}</>
  }
}

export function getByteImageSrc(image) {
  if (image) {
    let type = '';
    switch (getExtension(image.Name)) {
      case '.jpg':
      case '.jpeg':
        type = 'image/jpeg';
        break;
      case '.png':
        type = 'image/png';
        break;
      case '.bmp':
        type = 'image/bmp';
        break;
      default:
        type = 'image/jpeg';
        break;
    }
    return 'data:' + type + ';base64, ' + image.Data;
  }
}

/**
 * 异步文件下载功能
 * @param url 文件url地址
 * @param filename 导出的文件名
 */
export async function download({ method = 'get', url, sectionName, data, filename, save, messageText, errorMessageText = 'Error!' }) {
  message.info(messageText === undefined ? 'Downloading - ' + filename : messageText);

  var extension = filename.split('.').pop().toLowerCase();
  let isOpenFileInBrowser = false;
  switch (extension) {
    case 'pdf':
    case 'jpeg':
    case 'jpg':
    case 'png':
      if (save !== true) {
        isOpenFileInBrowser = true;
      }
      break;
  }

  if (isOpenFileInBrowser) {
    //新窗口打开pdf或者图片
    if (url.indexOf('?') >= 0) {
      url += '&filename=' + formatFileNameForUrl(filename);
    } else {
      url += '?filename=' + formatFileNameForUrl(filename);
    }
    var newWindow = window.open('/download' + url, '_blank');
  } else {
    //强制下载
    httpClient({
      method: method,
      url: url,
      data: data,
      responseType: 'blob'
    })
      .then(({ data }) => {
        if (data != null) {
          let blobUrl = URL.createObjectURL(data);
          saveFile(blobUrl, filename);
        } else {
          message.warning(errorMessageText);
        }
      })
      .catch(function (error) {
        message.warning(errorMessageText);
      });
  }
}

function openFileInBrowser(blobUrl, filename) {
  var newWindow = window.open(blobUrl);
  setTimeout(() => {
    if (newWindow !== null) {
      newWindow.document.title = filename;
    }
  }, 200);
}

export function isIOS() {
  // 判断是否为苹果移动设备
  return (
    (
      navigator.userAgent.indexOf('AppleWebKit') !== -1 || // 判断是否为AppleWebKit
      navigator.userAgent.indexOf('iPhone') !== -1 || // 判断是否为iPhone
      navigator.userAgent.indexOf('iPad') !== -1 || // 判断是否为iPad
      navigator.userAgent.indexOf('iPod') !== -1 // 判断是否为iPod
    ) && navigator.userAgent.indexOf('Android') === -1 && navigator.userAgent.indexOf('Windows') === -1
  );
}

export function isAndroid() {
  // 判断是否为Android设备
  return navigator.userAgent.indexOf('Android') !== -1;
}

export function isMobile() {
  // 判断是否为移动设备
  return (
    typeof window.orientation !== "undefined" || // 判断是否存在window.orientation属性，此属性在移动设备上一般存在
    navigator.userAgent.indexOf('IEMobile') !== -1 || // 判断是否为Windows Phone
    navigator.userAgent.indexOf('iPhone') !== -1 || // 判断是否为iPhone
    navigator.userAgent.indexOf('iPad') !== -1 || // 判断是否为iPad
    navigator.userAgent.indexOf('iPod') !== -1 || // 判断是否为iPod
    navigator.userAgent.indexOf('Android') !== -1 || // 判断是否为Android手机
    navigator.userAgent.indexOf('BlackBerry') !== -1 || // 判断是否为BlackBerry
    navigator.userAgent.indexOf('Opera Mini') !== -1 // 判断是否为Opera Mini浏览器
  );
}

export function saveFile(blobUrl, filename) {
  let link = document.createElement('a');
  link.download = filename;
  link.style.display = 'none';
  link.href = blobUrl;
  // 触发点击
  document.body.appendChild(link);
  link.click()
  // 移除
  document.body.removeChild(link);
}

//处理返回数据的错误信息
export function handleError(error, pageComponent) {
  console.log(error);
  console.log(pageComponent);
  switch (error.code) {
    case 401:
      const token = localStorage.getItem('token');
      if (token) {
        localStorage.removeItem('token');
        Modal.error({
          title: 'Session Expired',
          content: 'Click OK to login again. ',
          onOk() {
            window.location.href = '/';
          }
        });
      }
      break;
    case 'ERR_NETWORK':
      message.error('Network Error.');
      break;
    default:
      let errorMessage = error.stack.replace(/\n/g, '<br />');
      if (pageComponent?.props?.history !== undefined) {
        pageComponent.props.history.push('/error?type=UI&message=' + errorMessage);
      } else {
        window.location.href = '/error?type=UI&message=' + errorMessage;
      }
  }
}

//处理返回数据的错误信息
export function handleResultError(data, pageComponent) {
  console.log(data.code);
  console.log(pageComponent);
  switch (data.code) {
    case 401:
      const token = localStorage.getItem('token');
      if (token) {
        localStorage.removeItem('token');
        Modal.error({
          title: 'Session Expired',
          content: 'Click OK to login again. ',
          onOk() {
            window.location.href = '/';
          }
        });
      }
      break;
    case 'ERR_NETWORK':
      message.error('Network Error.');
      break;
    default:
      let errorMessage = data.message.replace(/\n/g, '<br />');
      if (pageComponent?.props?.history !== undefined) {
        pageComponent.props.history.push('/error?type=API&message=' + errorMessage);
      } else {
        window.location.href = '/error?type=API&message=' + errorMessage;
      }
      break;
  }
}

//获取名字首字母缩写
export function getAvatarName(name) {
  let result = '';
  let wordArray = name.split(' ');
  for (let i = 0; i < wordArray.length; i++) {
    if (wordArray[i].trim() !== '') {
      result += wordArray[i][0].toUpperCase();
    }
  }
  return result;
}

//null转空字符串
export function nullToEmpty(str) {
  return str !== null ? str : '';
}

//空字符串转null
export function emptyToNull(str) {
  return str !== '' ? str : null;
}

//空字符串转null
export function isNullOrEmpty(str) {
  return str === undefined || str === null || str.trim() === '';
}

//确认弹框
export const confirm = ({ title = 'Confirm',
  icon = <ExclamationCircleOutlined />,
  content = '',
  callback = null
}) => {
  Modal.confirm({
    title: title,
    icon: icon,
    content: content,
    onOk: callback
  });
};

//字符串格式化时间
export function formatDate(str) {
  var tmp = str.substring(0, 10).split('/');
  return new Date(tmp[2], tmp[1] - 1, tmp[0]);
}

//增加日期天数
export function addDate(startDate, addDays) {
  return new Date(startDate.setDate(startDate.getDate() + addDays));
}

//增加日期天数 - moment
export function addDateMoment(startDate, addDays) {
  let date = new Date(Date.parse(moment(startDate).format(config.format.sqlDate).replace(/-/g, "/")));
  let newDate = addDate(date, addDays);
  return moment(newDate, config.format.date);
}

//增加日期年份
export function addYear(startDate, addYears) {
  return new Date(startDate.setFullYear(startDate.getFullYear() + addYears));
}

//增加日期年份 - moment
export function addYearMoment(startDate, addYears) {
  let date = new Date(Date.parse(moment(startDate).format(config.format.sqlDate).replace(/-/g, "/")));
  let newDate = addYear(date, addYears);
  return moment(newDate, config.format.date);
}

//获取文件名(不包括扩展名)
export function getFileNameWithoutExtension(filename) {
  return filename.substring(0, filename.lastIndexOf("."));
}

//获取文件扩展名
export function getExtension(filename) {
  return filename.substring(filename.lastIndexOf("."), filename.length);
}

//获取以Page为查找基础的Section组件
export function getSectionComponent(pageComponent, sectionName) {
  let sectionComponent = null;
  if (pageComponent.createPage.current != null) {
    pageComponent.createPage.current?.child.map((section, i) => {
      if (section.current?.props.sectionName === sectionName) {
        sectionComponent = section.current;
      }
    })
  }
  return sectionComponent;
}

//设置上传文件的状态并返回文件列表
export function setFileList({ fileList, file, uploadProgress, status }) {
  fileList.map((oldFile, i) => {
    if (status !== undefined) {
      if (oldFile.name === file.name && oldFile.size === file.size) {
        oldFile.percent = uploadProgress;
      }
      if (oldFile.percent === 100) {
        oldFile.status = 'done';
      } else {
        oldFile.status = 'uploading';
      }
    } else {
      oldFile.status = status;
    }
    return oldFile;
  })
}

//设置控件是否可见
export function setUieVisible(pageComponent, sectionName, type, source, show) {
  let page = pageComponent.createPage.current?.state.page;
  page.sectionList.filter(c => c.name === sectionName)[0].componentList.filter(c => c.type === type)[0].groupList.map((group, i) => {
    group.uieList.map((uie, i) => {
      if (uie.source === source) {
        uie.show = show;
      }
      return uie;
    });
    return group;
  })
  pageComponent.createPage.current?.setState({ page });
}

//设置控件组是否可见
export function setGroupVisible(pageComponent, sectionName, type, groupType, show) {
  let page = pageComponent.createPage.current?.state.page;
  page.sectionList.filter(c => c.name === sectionName)[0].componentList.filter(c => c.type === type)[0].groupList.map((group, i) => {
    if (group.type === groupType) {
      group.show = show;
    }
    return group;
  })
  pageComponent.createPage.current?.setState({ page });
}

//替换Table的Uie属性, 多用于修改title
export function replaceUie({ tableComponent, componentType, uieSource, replaceParameter } = {}) {
  let columns = tableComponent.state.columns;
  columns = columns.map((column, i) => {
    replaceParameter.map((parameter, m) => {
      if (column[parameter.targetKey] === parameter.targetValue) {
        column[parameter.replaceKey] = parameter.replaceValue;
      }
    })
    return column;
  })
  tableComponent.setState({ columns });
}

//获取UTC时间字符串
export function getUTCDate() {
  let now = new Date();
  let str = now.getUTCFullYear() +
    '-' + ('0' + (now.getUTCMonth() + 1)).slice(-2) +
    '-' + ('0' + now.getUTCDate()).slice(-2) +
    ' ' + ('0' + now.getUTCHours()).slice(-2) +
    ':' + ('0' + now.getUTCMinutes()).slice(-2) +
    ':' + ('0' + now.getUTCSeconds()).slice(-2);
  return str;
}

//秒数转X时X分X秒
export function secondsToTime(inputSeconds) {
  let hours = Math.floor(inputSeconds / 3600);
  let minutes = Math.floor((inputSeconds % 3600) / 60);
  let seconds = inputSeconds % 60;
  let result = '';
  if (hours > 0) {
    result += hours + 'h ';
  }
  if (minutes > 0) {
    result += minutes + 'm ';
  }
  result += seconds + 's';
  return result;
}

//字符串每个首字母大写
export function titleCase(str) {
  var newStr = str.split(" "); for (var i = 0; i < newStr.length; i++) {
    newStr[i] = newStr[i].slice(0, 1).toUpperCase() + newStr[i].slice(1).toLowerCase();
  } return newStr.join(" ");
}

//获取文件的Base64
export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

//去掉文件名里，影响 url 的字符
export function formatFileNameForUrl(filename) {
  return filename.replace(/["<>%#&={}|\\^~[\]`]/g, '');
}

//解压zip里的字符串数据
export function decompressData(data) {
  let binaryString = window.atob(data);
  let binaryLen = binaryString.length;
  let bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    let ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }

  let jszip = new JSZip();
  return jszip.loadAsync(bytes).then(function (zip) {
    return zip.file("data.txt").async("string").then(function (data) {
      // data should now contain your JSON string
      let jsonData = JSON.parse(data);
      // Now jsonData should be your original object
      return jsonData;
    });
  });
}

//获取page的config
export function getPageConfig(webUIConfig, areaName, module, pageName, condition) {
  let config = null;
  let areas = webUIConfig.areaList.filter(item => item.name === areaName && item.module === module);
  if (areas.length > 0) {
    let pages = areas[0].pageList.filter(item => item.name === pageName && (item.condition == null || item.condition.split(',').map(c => c.trim()).includes(condition.toString())));
    if (pages.length > 0) {
      config = pages[0];
    }
  }
  return config;
}

export function formatPercent(percent) {
  const number = parseFloat(percent);
  return isNaN(number) ? 0 : number.toFixed(1);
}

//获取被禁止的日期
export function getDisabledDate(current, disabledDates, cutoffHours, cutoffMinutes) {
  // 确保比较的都是日期的开始部分
  const startOfCurrent = current.startOf('day');
  const todayStart = moment().startOf('day');

  // 禁用今天及今天之前的日期
  if (startOfCurrent <= todayStart) {
    return true;
  }

  // 禁用 disabledDates 数组中的日期
  if (disabledDates.some(disabledDate => current.isSame(disabledDate, 'day'))) {
    return true;
  }

  // 获取当前时间
  const now = new Date();
  const today = now.getDay(); // 周几 (0-6)
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // 禁用周六和周日
  if (current.day() === 0 || current.day() === 6) {
    return true;
  }

  // 如果当前时间是下午4点以后，禁用明天
  if ((currentHour > cutoffHours || (currentHour === cutoffHours && currentMinute >= cutoffMinutes)) && current.isSame(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1), 'day')) {
    return true;
  }

  // 如果今天是周六或周日，禁用下一个星期一
  if ((today === 0 || today === 6) && current.isSame(new Date(now.getFullYear(), now.getMonth(), now.getDate() + ((8 - today) % 7)), 'day')) {
    return true;
  }

  return false;
}

//去掉所有html标记
function removeHtmlTags(str) {
  return str.replace(/<[^>]*>/g, ' ');
}
